import { z } from 'zod';
import { DateOnlySchema, IdSchema, TimestampSchema } from './common.js';
import { MilestoneSchema } from './workflows.js';
import { NoteTargetTypeSchema } from './notes.js';
import { TaskSchema } from './tasks.js';
/** One entry of a record's history: a note, or an audit event with sanitized metadata. */
export const TimelineEntrySchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('note'),
        id: IdSchema,
        at: TimestampSchema,
        actorMembershipId: IdSchema.nullable(),
        actorDisplayName: z.string().nullable(),
        body: z.string(),
        pinned: z.boolean(),
    }),
    z.strictObject({
        kind: z.literal('event'),
        id: IdSchema,
        at: TimestampSchema,
        actorMembershipId: IdSchema.nullable(),
        actorDisplayName: z.string().nullable(),
        action: z.string(),
        targetType: z.string(),
        targetId: IdSchema.nullable(),
        metadata: z.record(z.string(), z.unknown()),
    }),
]);
export const TimelineQuerySchema = z.strictObject({
    targetType: NoteTargetTypeSchema,
    targetId: IdSchema,
    limit: z.coerce.number().int().min(1).max(100).default(50),
    /** Return entries strictly older than this instant. */
    before: TimestampSchema.optional(),
});
export const TimelineSchema = z.strictObject({
    items: z.array(TimelineEntrySchema),
    nextBefore: TimestampSchema.nullable(),
});
export const WorkRangeSchema = z.enum(['today', 'week', 'month', 'year', 'custom']);
export const MyWorkQuerySchema = z
    .strictObject({
    /** Defaults to the caller; other members need work_queue.read_all. */
    membershipId: IdSchema.optional(),
    range: WorkRangeSchema.default('week'),
    from: DateOnlySchema.optional(),
    to: DateOnlySchema.optional(),
})
    .refine((value) => value.range !== 'custom' || (value.from !== undefined && value.to !== undefined), {
    message: 'A custom range needs from and to',
    path: ['from'],
});
export const MyWorkSchema = z.strictObject({
    membershipId: IdSchema,
    range: z.strictObject({ from: DateOnlySchema, to: DateOnlySchema, timezone: z.string() }),
    counts: z.strictObject({
        openTasks: z.number().int().nonnegative(),
        dueToday: z.number().int().nonnegative(),
        overdue: z.number().int().nonnegative(),
        flagged: z.number().int().nonnegative(),
        openMilestones: z.number().int().nonnegative(),
    }),
    tasks: z.array(TaskSchema),
    milestones: z.array(MilestoneSchema),
});
